import { useState } from 'react';
import { ApiFailure, api } from './api.ts';

/**
 * REPLACING YOUR OWN PASSWORD, from the topbar — "users to be able to change
 * the passwords" (the owner). The current one is proven first, the new one is
 * typed twice, and the session doing the changing survives: only other
 * devices are signed out, and the success line says how many.
 */
export function ChangePassword({
  token,
  onClose,
}: {
  token: string;
  onClose: () => void;
}): JSX.Element {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);

  const ready = current.length > 0 && next.length >= 8 && next === repeat && !busy;

  async function submit(): Promise<void> {
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.changePassword(token, current, next);
      setDone(res.otherSessionsRevoked);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : 'That did not work. Try again.');
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={() => !busy && onClose()}>
      <div
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="change-pw-title"
        onClick={(e) => e.stopPropagation()}
      >
        {done !== null ? (
          <>
            <h2 id="change-pw-title">Password changed</h2>
            <p className="hint">
              {done === 0
                ? 'No other devices were signed in.'
                : `${done} other device${done === 1 ? ' was' : 's were'} signed out. This page stays signed in.`}
            </p>
            <div className="btn-row">
              <button className="btn-primary" type="button" style={{ width: 'auto' }} onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id="change-pw-title">Change your password</h2>
            <label className="field">
              <span className="field-label">Current password</span>
              <input
                className="input"
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                autoFocus
                disabled={busy}
              />
            </label>
            <label className="field">
              <span className="field-label">New password (at least 8 characters)</span>
              <input
                className="input"
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                disabled={busy}
              />
            </label>
            <label className="field">
              <span className="field-label">New password, again</span>
              <input
                className="input"
                type="password"
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                autoComplete="new-password"
                disabled={busy}
              />
            </label>
            {repeat.length > 0 && next !== repeat && (
              <p className="hint" style={{ color: 'var(--danger)' }}>
                The two copies do not match yet.
              </p>
            )}
            {error && (
              <div className="notice notice-warn" role="status">
                {error}
              </div>
            )}
            <p className="hint">Every other device you are signed in on will be signed out.</p>
            <div className="btn-row">
              <button className="btn-quiet" type="button" disabled={busy} onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn-primary"
                type="button"
                style={{ width: 'auto' }}
                disabled={!ready}
                onClick={() => void submit()}
              >
                {busy ? 'Changing…' : 'Change password'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
